import { cva } from 'class-variance-authority';

/**
 * Radio class recipes.
 *
 * Zero `dark:` variants, as everywhere else in this layer — and as of the 2026-09-15 rework, zero
 * theme-conditional CSS anywhere: every `--ds-radio-*` slot in `components/styles/design-system.css`
 * now points at a semantic role that inverts itself, so `.ds-radio` has no `:where(.dark)` block to
 * pair with, the way the field and popup classes already had none. Read that block first; it
 * explains what the rework changed and why, and `radioContrast.test.ts` asserts the claim so a
 * future slot pinned to a palette step cannot quietly reintroduce the dark rule.
 *
 * ── The geometry ───────────────────────────────────────────────────────────────────────────────
 *
 * Read off the variant's bound variables (Figma node 586:37614), all of them `📏 Spacing` steps:
 *
 *   a 24x24 area          `x6`      the hit target, and what the label centres against
 *   a circle at 18px      `x4,5`    inset 3px, with a 1px INSIDE stroke
 *   a circle at 10px      `x2,5`    the selected dot
 *   an 8px gap            `x2`      ring to label
 *   2px block padding     `x0,5`    which is what makes the row 28px tall
 *
 * An INSIDE stroke is the easy case: `border-box` sizing draws a 1px border inside an 18px box and
 * the numbers transfer unchanged. Note this is a CHANGE — the previous version read the stroke as
 * 1.5px centred, which needed the same 18px box but rendered half a pixel heavier. The checkbox
 * moved the same way in its own rework (#1184), so the pair is back in step: both now draw 1px
 * inside an 18px box, which is what keeps a radio and a checkbox on one baseline in the survey
 * answer lists the design pairs them in.
 *
 * ── Why hover is resolved on the ROW, not on the control ───────────────────────────────────────
 *
 * Figma's Hover cell moves three things at once: the ring's fill, the ring's STROKE, and the label's
 * colour. Hover is therefore a property of the whole row rather than of the 24px circle — which is
 * also the honest reading of a component whose label is a click target. Keying it off the control
 * would leave a user hovering the words with no response at all.
 *
 * So `group/rd-row` sits on the `<label>` and resolves state ONCE, into six `-current` slots that
 * the ring, the dot and the label text then simply read. Three reasons this shape rather than
 * stacked group variants on each element:
 *
 *   - the row can see `data-disabled` through `:has()` wherever it came from. That matters: a
 *     `<RadioGroup disabled>` marks each option's control WITHOUT setting the option's own
 *     `disabled` prop, so the cva-variant approach this replaces greyed the ring and left the label
 *     at full contrast. `DisabledGroup` covers it.
 *   - the three hover properties cannot drift out of step, because one selector sets all of them.
 *   - the selectors are mutually exclusive, so nothing depends on Tailwind's output order. The
 *     `:has()` precedent in this layer is `DatePicker`; the `-current` remap precedent is
 *     `TextInput`'s `--ds-input-stroke-extra`. Both used to name `TextArea` as well, and its Figma
 *     port removed both — the `:has([…:focus-visible])` outline, because a text control matches that
 *     on a mouse click, and the `-ring-current` remap along with the two tints it switched between.
 *
 * `group/rd` stays on `Radio.Root` for the two things only the control knows — selected and focus.
 * Two groups in one component follows `Accordion`.
 *
 * Precedence, and it is specificity rather than order: disabled (0,2,0) -> hover (0,3,0), with
 * selected read separately off `group/rd` because a row is hovered or not regardless of what is
 * chosen in it.
 */

/**
 * The wrapper, a real `<label>`, and the element that resolves every state.
 *
 * Cursor is set here and inherited, so ring and text agree.
 *
 * `items-start`, not the design's `CENTER`: a label long enough to wrap must leave the ring on the
 * first line rather than dragging it down to the centre of the whole text block. Survey answers are
 * the place this bites — they are full sentences at a narrow column width, a case Figma's 68px cell
 * cannot show. A single line renders identically to the centred version, which `LongLabel` measures;
 * see the note on `radioLabelRecipe` for the 2px that buys that.
 */
export const radioRootRecipe = cva([
  'ds-radio group/rd-row',
  'inline-flex w-fit cursor-pointer items-start gap-[8px] py-[2px]',

  // Resting, enabled. Out-specified by both rules below, never in competition with them.
  '[--ds-radio-bg-current:var(--ds-radio-bg)]',
  '[--ds-radio-border-current:var(--ds-radio-border)]',
  '[--ds-radio-on-current:var(--ds-radio-on)]',
  '[--ds-radio-dot-current:var(--ds-radio-dot)]',
  '[--ds-radio-label-current:var(--ds-radio-label)]',

  /**
   * Disabled, from the option OR from the group — which is the whole reason this is a `:has()` on
   * the row and not a cva variant. `border-disabled` serves the selected ring too; the dot greys to
   * its own lighter value. See the slot comment in `design-system.css`.
   */
  '[&:has([data-disabled])]:cursor-not-allowed',
  '[&:has([data-disabled])]:[--ds-radio-bg-current:var(--ds-radio-bg-disabled)]',
  '[&:has([data-disabled])]:[--ds-radio-border-current:var(--ds-radio-border-disabled)]',
  '[&:has([data-disabled])]:[--ds-radio-on-current:var(--ds-radio-border-disabled)]',
  '[&:has([data-disabled])]:[--ds-radio-dot-current:var(--ds-radio-dot-disabled)]',
  '[&:has([data-disabled])]:[--ds-radio-label-current:var(--ds-radio-label-disabled)]',

  // Hover, enabled. The dot does not move — Figma's Hover/checked cell keeps `background/accent`.
  '[&:hover:not(:has([data-disabled]))]:[--ds-radio-bg-current:var(--ds-radio-bg-hover)]',
  '[&:hover:not(:has([data-disabled]))]:[--ds-radio-border-current:var(--ds-radio-border-hover)]',
  '[&:hover:not(:has([data-disabled]))]:[--ds-radio-on-current:var(--ds-radio-on-hover)]',
  '[&:hover:not(:has([data-disabled]))]:[--ds-radio-label-current:var(--ds-radio-label-hover)]',
]);

/**
 * `Radio.Root` — the 24px hit area and the group the ring reads for selected and focus.
 *
 * It draws nothing of its own any more. The old version carried the focus halo as a background
 * here; the reworked design puts a two-layer ring on the 18px circle instead, so this element is
 * purely geometry and a state carrier.
 *
 * `focus-visible` rather than `focus`, so the ring answers a keyboard tab and not a mouse click —
 * the same call the checkbox makes, and the same reason: a ring left on every option the user merely
 * picks is noise. Inside a group this matters more than it does for a checkbox, because arrow-keying
 * through the options moves both the selection and the focus.
 */
export const radioControlRecipe = cva([
  'group/rd',
  'relative block size-[24px] shrink-0 cursor-[inherit] rounded-full',
  'bg-transparent outline-none',
]);

/**
 * The ring — a child of the root, because the focus shadow grows from the 18px circle and would be
 * the wrong size on the 24px one. At full spread it reaches 26px, 1px past the control on each side;
 * that is as authored, it is a shadow so it cannot touch layout, and nothing here clips.
 *
 * Filled at rest in BOTH themes, which is the rework's most visible change: the previous radio was
 * transparent until hover. A SELECTED ring is unfilled — the dot and the blue stroke carry it — with
 * one exception, below.
 */
export const radioRingRecipe = cva([
  'pointer-events-none absolute inset-[3px] rounded-full',
  'border border-solid',
  /**
   * Colour transitions only. `box-shadow` is deliberately NOT in this list: a focus indicator that
   * fades in over 150ms is decorative motion on the one affordance that has to be instantaneous, and
   * the house rule is that motion means the system is working. It also made `Focusing` read the
   * interpolation's start frame — an expanded, fully transparent shadow that looks exactly like a
   * ring that never rendered.
   */
  'transition-[border-color,background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

  // Unselected: both properties come straight off the row's resolved slots.
  'border-[var(--ds-radio-border-current)] bg-[var(--ds-radio-bg-current)]',

  /**
   * Selected. The stroke is `-on-current` (a different blue from the dot — `border/accent` against
   * `background/accent`) and the fill drops out, EXCEPT while focused, where it takes the surface
   * colour so the focus ring's opaque gap band has something to sit on. `:not(:focus-visible)` keeps
   * the two mutually exclusive rather than leaving the winner to Tailwind's output order.
   */
  'group-[[data-checked]]/rd:border-[var(--ds-radio-on-current)]',
  'group-[[data-checked]:not(:focus-visible)]/rd:bg-transparent',
  'group-[[data-checked]:focus-visible]/rd:bg-[var(--ds-radio-focus-backdrop)]',

  /**
   * Focus. One slot, composed in `design-system.css` from the eight `--effect-focus-*` geometry
   * tokens and two colours, exactly as Figma's `Focused` effect style composes it. The stroke keeps
   * its resting colour — see the WARNING in that block, which is where the contrast measurement and
   * the open question live.
   *
   * `[box-shadow:…]` rather than `shadow-[…]`, and this one cost real time. A bare arbitrary value
   * on `shadow-*` is AMBIGUOUS in Tailwind 4 — a `var()` could be a colour or a shadow list, and it
   * guesses COLOUR. The class is generated, the element gets a `box-shadow`, and every layer of it
   * is Tailwind's own transparent `0 0 #0000` placeholder: no error, no missing class, just no ring.
   * An arbitrary property cannot be misread, and `Focusing` counts the visible layers so a
   * regression here fails rather than merely looking wrong.
   */
  'group-focus-visible/rd:[box-shadow:var(--ds-radio-focus-ring)]',
]);

/**
 * `Radio.Indicator` — the dot, mounted only while selected.
 *
 * Centred by `m-auto` against `inset-0` rather than by a translate, so it cannot drift half a pixel
 * on a fractional layout the way a percentage offset can.
 */
export const radioDotRecipe = cva([
  'pointer-events-none absolute inset-0 m-auto size-[10px] rounded-full',
  'bg-[var(--ds-radio-dot-current)]',
]);

/**
 * The label text.
 *
 * `py-[2px]` is what makes `items-start` on the wrapper safe, exactly as on the checkbox: 14px text
 * on a 20px line against a 24px ring would top-align 2px high, and 2px of padding grows the FIRST
 * LINE BOX to 24px so it centres against the ring. A single-line radio therefore renders identically
 * to the `items-center` version Figma draws. `LongLabel` measures it.
 *
 * One colour, read from the row — no cva variant, because the row resolves disabled through
 * `:has()` and therefore also catches a `<RadioGroup disabled>` that never touches this prop.
 */
export const radioLabelRecipe = cva([
  'font-sans text-body-md select-none py-[2px]',
  'text-[var(--ds-radio-label-current)]',
  'transition-colors duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
]);

/**
 * The group.
 *
 * A column at the design's own 10px rhythm — node 586:38130's `Answers` frame, whose 28px rows sit
 * 38px apart. This was 8px before, which was a misreading of the same frame.
 */
export const radioGroupRecipe = cva(['flex flex-col gap-[10px]']);
